import { AppWindow, PencilIcon, TrashIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { OAuthClientForm, type OAuthClientPayload } from '@/forms/OAuthClientForm/OAuthClientForm'
import type { OAuthClient, OAuthClientCreated } from '@/interfaces/OAuth'
import API from '@/lib/api'
import { formatDate } from '@/lib/formats/formatDate'

const Secret = ({ label, value }: { label: string; value: string }) => (
  <div className="grid gap-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    <div className="flex items-start gap-2 rounded-md border bg-muted p-2 pl-3">
      <code className="min-w-0 flex-1 py-1.5 font-mono text-sm break-all">{value}</code>
      <CopyButton content={value} variant="outline" size="icon" />
    </div>
  </div>
)

export const OAuthClients = ({
  clients,
  mutate,
  creating = false,
  onCreatingChange,
}: {
  clients: OAuthClient[]
  mutate: () => void
  /** Řízeno zvenku: tlačítko Nová aplikace je v záhlaví sekce. */
  creating?: boolean
  onCreatingChange?: (creating: boolean) => void
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'settings.api.oauthClients' })

  const [editing, setEditing] = useState<OAuthClient | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [created, setCreated] = useState<OAuthClientCreated | null>(null)

  const closeForm = () => {
    setEditing(null)
    onCreatingChange?.(false)
  }

  const onCreate = (payload: OAuthClientPayload) =>
    API.post<OAuthClientCreated>('/api/v2/settings/oauth-clients', payload)
      .then((response) => {
        closeForm()
        setCreated(response.data)
        toast.success(t('actions.create.success'))
      })
      .catch(() => {
        toast.error(t('actions.create.error'))
      })

  const onEdit = (payload: OAuthClientPayload) =>
    API.put(`/api/v2/settings/oauth-clients/${editing?.id}`, payload)
      .then(() => {
        closeForm()
        mutate()
        toast.success(t('actions.edit.success'))
      })
      .catch(() => {
        toast.error(t('actions.edit.error'))
      })

  const onDelete = (id: string) =>
    API.delete(`/api/v2/settings/oauth-clients/${id}`)
      .then(() => {
        setDeletingId(null)
        mutate()
        toast.success(t('actions.delete.success'))
      })
      .catch(() => {
        toast.error(t('actions.delete.error'))
      })

  const onRevealClose = () => {
    setCreated(null)
    mutate()
  }

  return (
    <>
      <Dialog open={creating || editing !== null} onOpenChange={(open) => !open && closeForm()}>
        <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t(editing ? 'actions.edit.title' : 'actions.create.title')}</DialogTitle>
          </DialogHeader>
          {/* key: a new form state for each client */}
          <OAuthClientForm
            key={editing?.id ?? 'new'}
            defaultValues={editing ?? undefined}
            onSubmit={editing ? onEdit : onCreate}
            onCancel={closeForm}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={created !== null} onOpenChange={(open) => !open && onRevealClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('reveal.title')}</DialogTitle>
            <DialogDescription>{t('reveal.description')}</DialogDescription>
          </DialogHeader>
          {created && (
            <div className="grid gap-3">
              <Secret label="Client ID" value={created.clientId} />
              <Secret label="Client secret" value={created.clientSecret} />
            </div>
          )}
          <DialogFooter>
            <Button onClick={onRevealClose}>{t('reveal.dismiss')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {clients.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center">
          <AppWindow className="size-5 text-muted-foreground" />
          <p className="text-sm font-medium">Zatím žádné OAuth aplikace</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Aplikaci vytvoříte tlačítkem Nová aplikace. Client secret se zobrazí jen jednou, hned po vytvoření.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>{t('table.name')}</TableHead>
                <TableHead className="hidden md:table-cell">{t('table.clientId')}</TableHead>
                <TableHead className="hidden w-28 md:table-cell">{t('table.scopes')}</TableHead>
                <TableHead className="hidden w-32 md:table-cell">{t('table.createdAt')}</TableHead>
                <TableHead className="w-px" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => (
                <TableRow key={client.id} className="group">
                  <TableCell className="w-full max-w-0 md:w-auto md:max-w-none">
                    <span className="truncate font-medium">{client.name}</span>
                    {client.serverToServer && (
                      <Badge variant="secondary" className="ml-2">{t('serverToServerBadge')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="flex items-center gap-1">
                      {/* Full ID is in the copy button; the cell must not push the date out of the narrow settings page. */}
                      <code className="max-w-[12ch] truncate font-mono text-[13px]">{client.clientId}</code>
                      <CopyButton content={client.clientId} variant="ghost" size="icon" />
                    </div>
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground tabular md:table-cell">
                    {client.scopes.length}
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground tabular md:table-cell">
                    {formatDate(client.createdAt)}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1 opacity-60 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('actions.edit.title')}
                        onClick={() => setEditing(client)}
                      >
                        <PencilIcon />
                      </Button>
                      <ConfirmButton
                        title={t('actions.delete.title')}
                        description={t('actions.delete.description')}
                        open={deletingId === client.id}
                        onOpenChange={(open: boolean) => !open && setDeletingId(null)}
                        onConfirm={() => onDelete(client.id)}
                      >
                        <Button
                          variant="ghost"
                          size="icon"
                          className="hover:text-danger"
                          aria-label={t('actions.delete.title')}
                          onClick={() => setDeletingId(client.id)}
                        >
                          <TrashIcon />
                        </Button>
                      </ConfirmButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )
}
